/**
 * The canvas.
 *
 * Deliberately dependency-free beyond React and the pure modules under
 * src/lib/canvas: this same component is the flowchart in TWO apps,
 * and anything it imported from one of them would have to be forked into
 * the other, where it would then drift. Colours come from CSS variables
 * with literal fallbacks for the same reason.
 *
 * The document is CONTROLLED — `doc` in, `onChange` out — so the page
 * around it owns saving, undo and the runsheet round trip. A canvas that
 * owned its own history could not be undone in step with the rest of the
 * screen, which is how a chart and the table beside it end up disagreeing.
 *
 * WHY SVG. A section chain is read at 16% and edited at 400%. Only
 * vector geometry survives that: a CSS border rounds to nothing at one
 * end and to a slab at the other, and text laid out by the browser
 * reflows differently at every scale.
 */
import {
  memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  HANDLES, angleTo, boundsOf, centerX, centerY, cornersOf, distanceToPath,
  handlePoint, hitShape, normalizeAngle, outerBounds, rectFromPoints,
  resizeRect, rotatePoint, toWorld, zoomAbout,
} from '../../lib/canvas/geometry.js';
import {
  GRID, ROTATE_STEP, snapAlong, snapPoint, snapRect, snapResize,
} from '../../lib/canvas/guides.js';
import { clampFont, nearestFont } from '../../lib/canvas/fonts.js';
import {
  addEdge, addShape, duplicate, group, marqueeSelect, moveShapes, removeItems,
  selectedShapes, selectionBounds, shapeAt, ungroup, updateEdges, updateShapes, withGroups,
} from '../../lib/canvas/document.js';
import {
  routeEdge, pathFrom, pointAtFraction, fractionAtPoint, lengthOf,
  sidePoint, alongSide, nearestSide,
} from '../../lib/canvas/route.js';
import {
  canvasMeasurer, layoutText, firstBaseline, textAnchorX,
} from '../../lib/canvas/text.js';
import { cursorColour } from '../../lib/canvas/presence.js';
import { shapePath, dashOf } from './shapePath.js';
import './DiagramCanvas.css';

const NONE = Object.freeze([]);
// How far, in screen pixels, a pointer may sit off a connector and still
// be on it. A 2px line at 16% zoom is a third of a pixel — unclickable
// without this, and 16% is where a whole section is read.
const EDGE_SLOP = 7;
// Below this a drag is a click. Without it, selecting a shape nudges it
// by the pixel the pointer moved between down and up.
const DRAG_START = 3;
const ROTATE_ARM = 26;
// How far outside a shape's edge the connection ports sit. Also the slop
// the hover test uses, so moving onto a port does not lose the shape.
const PORT_OUT = 11;

/**
 * A note started by double-clicking bare canvas.
 *
 * Red and bold, and wider than it is tall because a remark is a sentence
 * rather than a label. No fill and no border: it is writing on the
 * chart, not another box on it.
 */
const NOTE = Object.freeze({ w: 240, h: 44, color: '#c0392b', fontSize: 14 });

/** Drop an endpoint within this of a face's middle and it IS the middle. */
const CENTRE_SNAP = 12;

/**
 * How near a shape an endpoint may be released and still attach to it.
 *
 * The hover test that lights a box up already reaches this far, so without
 * the same reach on the drop the box lit up, the button came up, and nothing
 * connected — and since an end released on nothing now comes away and stays
 * there, a near miss did not merely fail, it detached the line. A round shape
 * makes it worse: the ports sit on the bounding box, so the visible edge of a
 * circle is up to a fifth of its width inside where the drop is measured.
 */
const ATTACH_REACH = PORT_OUT + 8;
// How far outside the corner the document tag sits, in screen pixels.
// Diagonally out, where nothing else lives: the resize handles are ON the
// corners and the connection ports are at the edge midpoints.
const TAG_OUT = 12;
// The face the name on somebody else's cursor is drawn in, so the tab
// behind it can be measured rather than guessed at.
const PEER_FONT = '600 11px Inter, ui-sans-serif, system-ui, sans-serif';
const SIDES = Object.freeze(['top', 'right', 'bottom', 'left']);
// One measurer for the canvas. Made once because it owns a cache, and a
// cache made per render is a cache that never hits.
const measure = canvasMeasurer();

export default function DiagramCanvas({
  doc,
  onChange,
  selection = NONE,
  onSelectionChange,
  readOnly = false,
  onCommit,
  background = 'dots',
  onDropShape,
  viewRef,
  // Given, a box drawn from a recorded instrument gets a tag that opens
  // it. Not given, the tags do not appear at all — a chart drawn by hand
  // has nothing behind its boxes to open.
  onOpenDocument = null,
  // Ids the chain check has something to say about. Drawn as a halo
  // rather than as a change to the shape: a box that turned red when it
  // was flagged would lose the colour it was given on purpose, and on a
  // chart where pink means a life estate that is a real loss.
  flagged = NONE,
  // What this chart is, for anybody who cannot see it.
  ariaLabel = 'Chain of title',
  // Everybody else working this chain, and where to tell them the
  // pointer is. Both optional: a chart nobody shares works the same.
  peers = null,
  onPointerReport = null,
  // Changes when a chart has just been LOADED, and is the signal to fit
  // the whole thing on screen once. See the effect beside `fit` below
  // for why this is a prop rather than something the canvas works out.
  fitKey = null,
}) {
  const hostRef = useRef(null);
  const [viewport, setViewport] = useState({ x: 0, y: 0, scale: 1 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  // One object rather than several booleans: a drag, a resize and a
  // marquee are mutually exclusive, and separate flags let two of them
  // be true at once, which is how a canvas starts moving shapes while
  // rubber-banding.
  const [action, setAction] = useState(null);
  const [guides, setGuides] = useState({ lines: NONE, spacing: NONE, sizes: NONE });
  const [editing, setEditing] = useState(null);
  // A note just made by double-clicking bare canvas, still empty. Held so
  // that leaving it empty can take it away again.
  const [blankNote, setBlankNote] = useState(null);
  // The conveyance whose label is being typed. Separate from `editing`
  // because a connector's label is not inside a box — it floats on the
  // line — so the two editors are positioned by different arithmetic.
  const [editingEdge, setEditingEdge] = useState(null);
  const [hover, setHover] = useState(null);
  const pointerAt = useRef(null);

  const marked = useMemo(() => new Set(flagged), [flagged]);

  // Sorted once per document rather than on every render. A drag renders
  // on every pointer move, and re-sorting three hundred boxes each time
  // is work done for nothing sixty times a second.
  const drawn = useMemo(
    () => [...doc.shapes].sort((a, b) => a.z - b.z), [doc.shapes],
  );

  const shapesById = useMemo(
    () => new Map(doc.shapes.map((s) => [s.id, s])), [doc.shapes],
  );
  const rectFor = useCallback((id) => {
    const s = shapesById.get(id);
    return s ? { x: s.x, y: s.y, w: s.w, h: s.h } : null;
  }, [shapesById]);

  // Routes are recomputed whenever anything moves, which is the point:
  // an edge is a function of its shapes' current rects, never a stored
  // pair of coordinates that a drag can leave behind.
  const routes = useMemo(() => doc.edges.map((edge) => ({
    edge, points: routeEdge(edge, rectFor),
  })), [doc.edges, rectFor]);

  // The routes by edge id, so the marquee can ask where a connector
  // actually runs without solving every route again per frame.
  const routesById = useMemo(
    () => new Map(routes.map((r) => [r.edge.id, r.points])), [routes],
  );

  const chosen = useMemo(() => new Set(selection), [selection]);
  const bounds = useMemo(() => selectionBounds(doc, selection), [doc, selection]);
  // A single unrotated shape shows handles in its own frame; a group, or
  // anything turned, gets an upright box, because eight handles on a
  // rotated multi-selection resize in directions nobody can predict.
  const only = selection.length === 1 ? shapesById.get(selection[0]) : null;
  const frame = only && !only.rotation
    ? { x: only.x, y: only.y, w: only.w, h: only.h, rotation: 0 }
    : (only ? { x: only.x, y: only.y, w: only.w, h: only.h, rotation: only.rotation } : bounds);

  /**
   * Where on a box a line being dragged would attach.
   *
   * ONE function, used by the preview and by the drop, so what is shown
   * and what happens cannot disagree — which is the whole reason the
   * ports are drawn at all.
   *
   * The side is the face pointing at the pointer; the position along it
   * is the pointer's own, snapped to the grid. Sliding along the side is
   * what stops three deeds into one party all arriving at the same
   * point, and the grid is what stops the three of them being a few
   * pixels apart instead of level.
   */
  const attachTo = useCallback((shape, at) => {
    const rect = { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
    // The NEAREST side, not the one the pointer is most in the direction
    // of: dropping five pixels under a wide box's top edge means the top
    // edge, whatever the arithmetic from the centre says.
    const side = nearestSide(rect, at);
    const onGrid = snapPoint(at);
    // Only the coordinate that runs ALONG the side is taken from the
    // snapped point; the other one is whatever the box's edge is.
    const along = alongSide(rect, side, onGrid);
    // DROPPED NEAR THE MIDDLE MEANS THE MIDDLE. The point is snapped to
    // the grid, but a box is not: one whose width or left edge is not a
    // whole number of grid steps turns a drop aimed at the centre into a
    // fraction a few pixels off it, and a few pixels off is exactly what
    // a kink is made of. The middle is where nearly every connector is
    // meant to land, so it is the one position worth pulling to.
    const span = (side === 'top' || side === 'bottom') ? rect.w : rect.h;
    const centred = Math.abs(along - 0.5) * span <= CENTRE_SNAP;
    const settled = centred ? 0.5 : along;
    return { side, along: settled, at: sidePoint(rect, side, settled) };
  }, []);

  /**
   * Where a line being dragged would attach, if it were let go now.
   *
   * Worked out once, here, because three things want the same answer and
   * they must not disagree: the ports drawn on the box being dragged
   * onto, which of those is lit, and the end of the dashed preview. The
   * side is `faceToward`, which is also what pointer-up commits — so
   * what is shown is what happens.
   */
  const landing = useMemo(() => {
    if (action?.type !== 'connect' && action?.type !== 'endpoint') return null;
    const shape = hover ? shapesById.get(hover) : null;
    if (!shape) return null;
    return { shape, ...attachTo(shape, action.at) };
  }, [action, hover, shapesById, attachTo]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const measure = () => setSize({ w: host.clientWidth, h: host.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  /**
   * Bring a handful of boxes into view.
   *
   * The scale is kept where somebody put it unless what they asked for
   * does not fit — a check that zoomed the whole chart out to fit two
   * boxes would throw away the reading position on every click down the
   * list.
   */
  const look = useCallback((ids) => {
    const wanted = doc.shapes.filter((s0) => ids.includes(s0.id));
    const box = boundsOf(wanted.map(outerBounds));
    if (!box || !size.w) return;
    const pad = 90;
    const scale = Math.min(
      viewport.scale,
      (size.w - pad * 2) / Math.max(box.w, 1),
      (size.h - pad * 2) / Math.max(box.h, 1),
    );
    setViewport({
      scale: Math.max(scale, 0.05),
      x: size.w / 2 - (box.x + box.w / 2) * scale,
      y: size.h / 2 - (box.y + box.h / 2) * scale,
    });
  }, [doc.shapes, size, viewport.scale]);

  // Published rather than lifted into the parent: the viewport is this
  // component's own business, and hoisting it would re-render the whole
  // editor on every pixel of a pan.
  useEffect(() => {
    if (!viewRef) return;
    viewRef.current = {
      centre: () => toWorld(viewport, size.w / 2, size.h / 2),
      // The last place the pointer was, in world space, so a paste can
      // land under it. Kept on the ref rather than in state: it changes
      // on every mouse move and nothing renders from it.
      get pointer() { return pointerAt.current; },
      viewport,
      // The live drawing and the routes behind it, so an export can take
      // a copy of what is actually on screen rather than painting the
      // document a second time and slowly drifting from it.
      surface: () => hostRef.current?.querySelector('.dgm-surface') || null,
      get routes() { return routes; },
      look,
    };
  }, [viewRef, viewport, size, routes, look]);

  const pointerWorld = useCallback((event) => {
    const box = hostRef.current.getBoundingClientRect();
    return toWorld(viewport, event.clientX - box.left, event.clientY - box.top);
  }, [viewport]);

  const setSelection = useCallback((ids) => onSelectionChange?.(ids), [onSelectionChange]);

  /* ------------------------------------------------------- viewport -- */

  const onWheel = useCallback((event) => {
    event.preventDefault();
    const box = hostRef.current.getBoundingClientRect();
    const px = event.clientX - box.left;
    const py = event.clientY - box.top;
    // Ctrl/⌘ + wheel is zoom everywhere, and a trackpad pinch arrives as
    // exactly that. A plain wheel pans, which is what a large diagram
    // wants far more often than zoom.
    if (event.ctrlKey || event.metaKey) {
      setViewport((v) => zoomAbout(v, px, py, v.scale * Math.exp(-event.deltaY * 0.0015)));
      return;
    }
    setViewport((v) => ({ ...v, x: v.x - event.deltaX, y: v.y - event.deltaY }));
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    // Non-passive, or preventDefault cannot stop the page scrolling
    // behind the canvas — React's onWheel is passive by default.
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const fit = useCallback(() => {
    const box = boundsOf(doc.shapes.map(outerBounds));
    if (!box || !size.w) return;
    const pad = 80;
    const scale = Math.min(
      (size.w - pad * 2) / Math.max(box.w, 1),
      (size.h - pad * 2) / Math.max(box.h, 1),
      2,
    );
    setViewport({
      scale,
      x: size.w / 2 - (box.x + box.w / 2) * scale,
      y: size.h / 2 - (box.y + box.h / 2) * scale,
    });
  }, [doc.shapes, size]);

  /**
   * Fit the whole chart on screen the first time it is opened.
   *
   * Mays: "when i first open a flowchart, can it already be fit to the
   * screen to see it all?"
   *
   * It used to open at scale 1 centred on the world origin, which is
   * only where the chart is if somebody happened to draw it there. A
   * chain of title built from a runsheet lands wherever the layout put
   * it, so opening one meant hunting for it with the scroll wheel
   * before reading a word of it — on every open, of every chart.
   *
   * WHY THE SIGNAL COMES FROM OUTSIDE
   *
   * The tempting version fits as soon as this component sees a
   * non-empty doc. That is wrong on a chart that starts EMPTY: the
   * first box somebody draws would be a non-empty doc arriving, and the
   * view would jump and zoom to 2x around that one box as they placed
   * it.
   *
   * The canvas cannot tell those apart — a shape is a shape. The loader
   * can: it knows whether shapes came out of the database or out of
   * somebody's hands. So it hands down a key that changes when a
   * document has been LOADED, and this fits once per key. A chart that
   * loads empty never gets a key, and so is never yanked out from under
   * the person starting it.
   *
   * Waits on a measured host, because fit divides by size and a
   * ResizeObserver has not fired on the first paint.
   */
  const fittedKey = useRef(null);
  useEffect(() => {
    if (fitKey === null || fitKey === undefined) return;
    if (fittedKey.current === fitKey) return;
    if (!size.w || !size.h) return;
    if (!doc.shapes.length) return;
    fittedKey.current = fitKey;
    fit();
  }, [fitKey, size, doc.shapes, fit]);

  /* --------------------------------------------------- interactions -- */

  const commit = useCallback((next, label) => {
    onChange?.(next);
    onCommit?.(label);
  }, [onChange, onCommit]);

  const beginDrag = (event, shape) => {
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;
    let ids = selection;
    if (!chosen.has(shape.id)) {
      ids = additive ? withGroups(doc, [...selection, shape.id]) : withGroups(doc, [shape.id]);
      setSelection(ids);
    } else if (additive) {
      ids = selection.filter((id) => id !== shape.id);
      setSelection(ids);
      return;
    }
    if (readOnly) return;
    setAction({
      type: 'drag', ids, start: pointerWorld(event), moved: false, doc,
    });
  };

  const onPointerDown = (event) => {
    // The zoom bar and the text editor live INSIDE the canvas so they
    // scroll and theme with it. Without this the canvas captures the
    // pointer on the way down and their clicks never land — the buttons
    // look enabled and do nothing.
    // Whatever else happens, this chart is now the one being worked in.
    hostRef.current?.focus?.({ preventScroll: true });
    if (event.target.closest?.('.dgm-ui')) return;
    if (event.button === 1 || event.button === 2 || event.altKey) {
      setAction({ type: 'pan', start: { x: event.clientX, y: event.clientY }, from: viewport });
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const world = pointerWorld(event);
    const slop = EDGE_SLOP / viewport.scale;

    const shape = shapeAt(doc, world, hitShape);
    if (shape) { beginDrag(event, shape); return; }

    const near = routes.find((r) => distanceToPath(world, r.points) <= slop);
    if (near) {
      // Shift builds a selection here as it does on a box. Without this
      // a connector could be added to nothing and removed from nothing:
      // clicking one threw away whatever was held, so a branch could
      // never be gathered up by hand.
      const additive = event.shiftKey || event.metaKey || event.ctrlKey;
      if (!additive) setSelection([near.edge.id]);
      else if (chosen.has(near.edge.id)) setSelection(selection.filter((id) => id !== near.edge.id));
      else setSelection([...selection, near.edge.id]);
      return;
    }

    if (!event.shiftKey) setSelection([]);
    setAction({ type: 'marquee', start: world, at: world, base: event.shiftKey ? selection : [] });
  };

  /**
   * Dragging a new connector out of a shape's edge.
   *
   * The point names a SIDE rather than a port: the connector remembers
   * which face it left from, and everything else about where the line
   * runs is re-solved from the shapes' current rects. That is what makes
   * an arrow follow its boxes instead of tearing off them.
   */
  const onConnectDown = (event, shape, side) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setAction({
      type: 'connect',
      from: { shapeId: shape.id, side },
      at: pointerWorld(event),
      doc,
    });
  };

  /**
   * Bending a connector by hand.
   *
   * A waypoint OUTRANKS the router: a route somebody arranged around a
   * box in the way must not be re-derived out from under them the next
   * time anything moves.
   */
  const onWaypointDown = (event, edge, index, insertAt) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    let working = doc;
    let at = index;
    if (insertAt) {
      const points = [...(edge.waypoints || [])];
      points.splice(index, 0, insertAt);
      working = updateEdges(doc, [edge.id], { waypoints: points });
      onChange?.(working);
      at = index;
    }
    setAction({ type: 'waypoint', edgeId: edge.id, index: at, doc: working });
  };

  /**
   * Sliding the instrument along the line it belongs to.
   *
   * Where a label sits is a real decision on a dense chart: the middle
   * of one line is on top of the next one along, and the only way out is
   * to move it. Stored as a FRACTION so it keeps its place when the
   * boxes either end move and the route is re-solved.
   */
  const onLabelDown = (event, edge, points) => {
    if (readOnly) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelection([edge.id]);
    setAction({ type: 'label', edgeId: edge.id, points, doc });
  };

  /**
   * Taking one end of a line somewhere else.
   *
   * Nothing changes until it is let go. A line that re-attached on every
   * pointer move would walk across the chart through every box between
   * where it started and where it is going, and each of those is a route
   * re-solved and a document rebuilt.
   */
  const onEndDown = (event, edge, which, points) => {
    if (readOnly) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const anchorAt = which === 'from' ? points[points.length - 1] : points[0];
    setAction({
      type: 'endpoint',
      edgeId: edge.id,
      which,
      keeps: which === 'from' ? edge.to?.shapeId : edge.from?.shapeId,
      anchor: anchorAt,
      at: pointerWorld(event),
      doc,
    });
  };

  const onHandleDown = (event, handle) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setAction({
      type: handle === 'rotate' ? 'rotate' : 'resize',
      handle,
      start: pointerWorld(event),
      doc,
      ids: selection,
      frame,
    });
  };

  const onPointerMove = (event) => {
    pointerAt.current = pointerWorld(event);
    // In WORLD coordinates, so it lands on the same box for everybody
    // however they happen to be panned and zoomed.
    onPointerReport?.(pointerAt.current);
    if (!action) {
      const world = pointerWorld(event);
      // Hover is tested with SLOP, and the slop is the distance the
      // connection ports sit outside the shape. Without it, moving the
      // pointer towards a port leaves the shape, the hover clears, and
      // the ports vanish a moment before you reach them — so the one
      // gesture they exist for is the one gesture that cannot be made.
      const reach = (PORT_OUT + 8) / viewport.scale;
      const shape = shapeAt(doc, world, (s, at) => hitShape(s, at, reach));
      setHover(shape ? shape.id : null);
      return;
    }

    if (action.type === 'pan') {
      setViewport({
        ...action.from,
        x: action.from.x + (event.clientX - action.start.x),
        y: action.from.y + (event.clientY - action.start.y),
      });
      return;
    }

    const world = pointerWorld(event);

    if (action.type === 'marquee') {
      setAction({ ...action, at: world });
      const rect = rectFromPoints(action.start, world);
      const caught = marqueeSelect(doc, rect, (id) => routesById.get(id));
      setSelection(withGroups(doc, [...new Set([...action.base, ...caught])]));
      return;
    }

    if (action.type === 'drag') {
      let dx = world.x - action.start.x;
      let dy = world.y - action.start.y;
      if (!action.moved && Math.hypot(dx, dy) * viewport.scale < DRAG_START) return;
      // Shift holds the drag to one axis, which is how a box is nudged
      // back into line with the one above it without losing the column
      // it is already in. The axis is whichever the pointer has moved
      // further along, decided every frame so it can be changed mid-drag
      // by pulling the other way.
      if (event.shiftKey) {
        if (Math.abs(dx) >= Math.abs(dy)) dy = 0; else dx = 0;
      }

      const moving = selectedShapes(action.doc, action.ids);
      const box = boundsOf(moving.map(outerBounds));
      const others = action.doc.shapes
        .filter((s) => !action.ids.includes(s.id))
        .map(outerBounds);
      // Alt is the escape from all of it — neighbours, spacing and the
      // grid — for the one box that genuinely belongs half a step out.
      // HELD DURING THE DRAG: alt pressed before grabbing anything is
      // the pan gesture above, and that is deliberate — panning with alt
      // is how somebody moves around a chart bigger than the screen.
      const snap = event.altKey
        ? { dx: 0, dy: 0, lines: NONE, spacing: NONE }
        : snapRect({ ...box, x: box.x + dx, y: box.y + dy }, others, viewport.scale);

      setGuides({ lines: snap.lines, spacing: snap.spacing, sizes: NONE });
      setAction({ ...action, moved: true });
      onChange?.(moveShapes(action.doc, action.ids, dx + snap.dx, dy + snap.dy));
      return;
    }

    if (action.type === 'endpoint') {
      setAction({ ...action, at: world });
      // The box it would land on, highlighted so it is obvious before
      // letting go. Never the box at the other end — a line from a party
      // to itself is not a conveyance. The SAME reach the drop uses, or
      // the highlight promises something the release does not do.
      const over = shapeAt(doc, world, (s2, at) => hitShape(s2, at, ATTACH_REACH / viewport.scale));
      setHover(over && over.id !== action.keeps ? over.id : null);
      return;
    }

    if (action.type === 'connect') {
      setAction({ ...action, at: world });
      // The shape under the pointer is highlighted as a target, so it is
      // obvious what the line will attach to before letting go.
      const over = shapeAt(doc, world, (s2, at) => hitShape(s2, at, ATTACH_REACH / viewport.scale));
      setHover(over && over.id !== action.from.shapeId ? over.id : null);
      return;
    }

    if (action.type === 'label') {
      // Projected onto the line rather than measured from an end, so a
      // label dragged out sideways slides to the nearest part of it.
      const along = fractionAtPoint(action.points, world);
      // Pulled to the middle, or to a quarter along — the places a
      // conveyance label sits on every chart anybody has ever drawn.
      // Never quantised: the reason somebody drags a label is that the
      // usual place is under a line crossing it, and rounding would take
      // away the only move that resolves that.
      const at = event.altKey
        ? along
        : snapAlong(along, lengthOf(action.points), viewport.scale);
      onChange?.(updateEdges(action.doc, [action.edgeId], { labelPos: at }));
      return;
    }

    if (action.type === 'waypoint') {
      // On the grid, like everything else that is dragged. A bend three
      // units out of true is visible from across the room on a chart of
      // forty conveyances — the dog-legs are what the eye follows. Alt
      // held through the drag puts it exactly where the pointer is.
      const bend = event.altKey ? world : snapPoint(world);
      onChange?.(updateEdges(action.doc, [action.edgeId], (e) => {
        const points = [...(e.waypoints || [])];
        points[action.index] = bend;
        return { waypoints: points };
      }));
      return;
    }

    if (action.type === 'resize') {
      const dx = world.x - action.start.x;
      const dy = world.y - action.start.y;
      const raw = resizeRect(action.frame, action.handle, dx, dy, {
        keepAspect: event.shiftKey, minSize: 4,
      });
      // Snapped to the sizes and edges already on the chart, so a column
      // of party boxes ends up EXACTLY the same width rather than nearly.
      // Held off while alt is down, the same escape as a move; and while
      // shift is down, because snapping one side of a locked aspect
      // ratio would break the ratio the key was pressed to keep.
      const standing = action.doc.shapes
        .filter((s) => !action.ids.includes(s.id))
        .map(outerBounds);
      const fit = event.altKey || event.shiftKey
        ? { rect: raw, lines: NONE, sizes: NONE }
        : snapResize(raw, action.handle, standing, viewport.scale);
      setGuides({ lines: fit.lines, spacing: NONE, sizes: fit.sizes });
      const next = { ...fit.rect, w: Math.max(4, fit.rect.w), h: Math.max(4, fit.rect.h) };
      // A TEXT BLOCK's own size follows its box, and lands on the ladder
      // the toolbar steps through — so a block dragged to a size and one
      // typed to a size are the same size. By HEIGHT only: dragging the
      // east handle of a text block is somebody making it wrap
      // differently, not making the words bigger. Boxed shapes are left
      // alone — resizing a party box is making room, not shouting.
      const holdsHeight = /^[ns]/.test(action.handle);
      const scaleFont = (shape) => {
        if (shape.shapeType !== 'text' || !holdsHeight) return {};
        const grew = next.h / Math.max(action.frame.h, 1e-6);
        const wanted = (Number(shape.fontSize) || 10) * grew;
        return { fontSize: event.altKey ? clampFont(wanted) : nearestFont(wanted) };
      };

      if (action.ids.length === 1) {
        // One shape IS the frame, so it takes the snapped rect as it
        // stands. Going through the ratio below would multiply and then
        // divide by the same number and hand back 160.00000000000003 —
        // which is then the width in the document, in the size field and
        // in the export.
        onChange?.(updateShapes(action.doc, action.ids, (shape) => ({
          ...next,
          ...scaleFont(shape),
        })));
      } else {
        const sx = next.w / Math.max(action.frame.w, 1e-6);
        const sy = next.h / Math.max(action.frame.h, 1e-6);
        // Scaled about the frame, so resizing a multi-selection moves the
        // shapes apart proportionally instead of resizing each in place.
        onChange?.(updateShapes(action.doc, action.ids, (s) => ({
          x: next.x + (s.x - action.frame.x) * sx,
          y: next.y + (s.y - action.frame.y) * sy,
          w: Math.max(4, s.w * sx),
          h: Math.max(4, s.h * sy),
          ...scaleFont(s),
        })));
      }
      return;
    }

    if (action.type === 'rotate') {
      const cx = centerX(action.frame);
      const cy = centerY(action.frame);
      // Every fifteen degrees by default, which is the same decision the
      // grid makes: a box at 3° reads as a mistake, and the angles a
      // chain of title actually uses are upright, square and the
      // diagonals between. Alt turns it freely, the escape everything
      // else here has; shift still snaps, because it always did and
      // somebody's hand knows that.
      const angle = normalizeAngle(
        angleTo(cx, cy, world.x, world.y),
        event.altKey && !event.shiftKey ? 0 : ROTATE_STEP,
      );
      onChange?.(updateShapes(action.doc, action.ids, (s) => {
        if (action.ids.length === 1) return { rotation: angle };
        // A group turns about the group's centre, so the shapes orbit
        // rather than each spinning where it stands.
        const was = action.doc.shapes.find((o) => o.id === s.id);
        const spun = rotatePoint(centerX(was), centerY(was), cx, cy, angle - (was.rotation || 0));
        return {
          rotation: normalizeAngle((was.rotation || 0) + angle),
          x: spun.x - s.w / 2,
          y: spun.y - s.h / 2,
        };
      }));
    }
  };

  const onPointerUp = (event) => {
    if (action?.type === 'connect') {
      const world = pointerWorld(event);
      const reach = ATTACH_REACH / viewport.scale;
      const target = shapeAt(action.doc, world, (s2, at) => hitShape(s2, at, reach));
      // A LINE MAY END NOWHERE. Dropping on empty canvas used to throw
      // the whole gesture away, so a chain drawn top-down — deed out of
      // a party, then the party it went to — had to be built in the one
      // order the tool allowed, and an arrow could never be parked while
      // its other end was worked out. It lands as a free point instead,
      // which the router already understands: an end with coordinates
      // and no shape is drawn where it was left, and dragging it onto a
      // box later attaches it for good.
      if (!target || target.id === action.from.shapeId) {
        const out = addEdge(action.doc, {
          from: action.from,
          to: { x: world.x, y: world.y },
        });
        onChange?.(out.doc);
        onCommit?.('Connect');
        setSelection([out.edge.id]);
        setAction(null);
        setHover(null);
        return;
      }
      {
        const { side, along } = attachTo(target, world);
        const out = addEdge(action.doc, {
          from: action.from,
          to: { shapeId: target.id, side, along },
        });
        onChange?.(out.doc);
        onCommit?.('Connect');
        setSelection([out.edge.id]);
        // A conveyance IS its instrument — volume and page, the date,
        // what moved and over which land. Opening the label the moment
        // the line is drawn is the difference between a chart that
        // records deeds and one that records arrows: nobody comes back
        // to fill these in afterwards.
        setEditingEdge(out.edge.id);
      }
      setAction(null);
      setHover(null);
      return;
    }
    if (action?.type === 'endpoint') {
      const world = pointerWorld(event);
      const reach = ATTACH_REACH / viewport.scale;
      const target = shapeAt(action.doc, world, (s2, at) => hitShape(s2, at, reach));
      // Pulled off onto empty canvas, an end comes away and stays where
      // it was put — the same rule as drawing one. Dropping it back on
      // the box at the other end is the one move refused, because a
      // conveyance from a party to itself is not a conveyance.
      if (!target) {
        commit(
          updateEdges(action.doc, [action.edgeId], {
            [action.which]: { x: world.x, y: world.y },
          }),
          'Detach endpoint',
        );
        setAction(null);
        setHover(null);
        return;
      }
      if (target.id !== action.keeps) {
        const { side, along } = attachTo(target, world);
        commit(
          updateEdges(action.doc, [action.edgeId], {
            [action.which]: { shapeId: target.id, side, along },
          }),
          // Dropped back on the box it was already on, this is somebody
          // sliding the arrow along that box's edge rather than moving
          // it to another party — which is a different sentence to see
          // in the undo list.
          action.doc.edges.find((e) => e.id === action.edgeId)?.[action.which]?.shapeId === target.id
            ? 'Move endpoint'
            : 'Reconnect',
        );
      }
      setAction(null);
      setHover(null);
      return;
    }

    if (action?.type === 'label') onCommit?.('Move label');
    if (action?.type === 'waypoint') onCommit?.('Bend');
    if (action?.type === 'drag' && action.moved) onCommit?.('Move');
    if (action?.type === 'resize') onCommit?.('Resize');
    if (action?.type === 'rotate') onCommit?.('Rotate');
    setAction(null);
    setGuides({ lines: NONE, spacing: NONE, sizes: NONE });
  };

  /* ---------------------------------------------------------- keys -- */

  useEffect(() => {
    const onKey = (event) => {
      const inField = /^(INPUT|TEXTAREA)$/.test(event.target.tagName) || event.target.isContentEditable;
      if (inField) return;
      // ONLY THE CHART BEING WORKED IN. These listen on window, because a
      // div is not focusable by default and an onKeyDown on one never
      // fires — but window means every chart on the page hears every key.
      // An agent with two tracts open had one box selected in each, and
      // Delete removed both; opening a deed over the chart and pressing
      // an arrow to page through it nudged the box underneath and saved
      // the move. So the canvas takes focus when it is clicked and each
      // one answers only while the focus is inside it.
      if (!hostRef.current?.contains(document.activeElement)) return;
      const meta = event.metaKey || event.ctrlKey;

      if (meta && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        // Connectors too. A chain is boxes AND the deeds between them,
        // and a select-all that quietly left the conveyances behind made
        // "restyle everything" a job that had to be done twice.
        setSelection([...doc.shapes.map((s) => s.id), ...doc.edges.map((e) => e.id)]);
        return;
      }
      if (!selection.length || readOnly) return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        commit(removeItems(doc, selection), 'Delete');
        setSelection([]);
        return;
      }
      if (meta && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        const out = duplicate(doc, selection);
        commit(out.doc, 'Duplicate');
        setSelection(out.ids);
        return;
      }
      if (meta && event.key.toLowerCase() === 'g') {
        event.preventDefault();
        commit(event.shiftKey ? ungroup(doc, selection) : group(doc, selection), 'Group');
        return;
      }
      // Type styling on whatever is held, boxes and connector labels
      // alike — a chain gets marked up while it is being read, and
      // reaching for a style bar to embolden one party breaks the reading.
      // Toggled off the FIRST selected item so a mixed selection lands
      // somewhere definite: press once and everything matches, press
      // again and everything clears.
      const MARKS = { b: 'bold', i: 'italic', u: 'underline' };
      const mark = meta ? MARKS[event.key.toLowerCase()] : null;
      if (mark) {
        event.preventDefault();
        const held = [...doc.shapes, ...doc.edges].filter((one) => chosen.has(one.id));
        if (!held.length) return;
        const next = !held[0][mark];
        const shapeIds = held.filter((one) => one.kind === 'shape').map((one) => one.id);
        const edgeIds = held.filter((one) => one.kind === 'edge').map((one) => one.id);
        let out = doc;
        if (shapeIds.length) out = updateShapes(out, shapeIds, { [mark]: next });
        if (edgeIds.length) out = updateEdges(out, edgeIds, { [mark]: next });
        commit(out, mark.charAt(0).toUpperCase() + mark.slice(1));
        return;
      }
      if (event.key.startsWith('Arrow')) {
        event.preventDefault();
        const step = event.shiftKey ? 10 : 1;
        const dx = (event.key === 'ArrowRight' ? step : 0) - (event.key === 'ArrowLeft' ? step : 0);
        const dy = (event.key === 'ArrowDown' ? step : 0) - (event.key === 'ArrowUp' ? step : 0);
        commit(moveShapes(doc, selection, dx, dy), 'Nudge');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, selection, chosen, readOnly, commit, setSelection]);

  /* -------------------------------------------------------- render -- */

  const screen = (wx, wy) => ({
    x: wx * viewport.scale + viewport.x,
    y: wy * viewport.scale + viewport.y,
  });

  const editShape = editing ? shapesById.get(editing) : null;
  // Where on the line the label sits, recomputed from the CURRENT route
  // so the editor stays on the line if anything moves under it.
  const editEdge = useMemo(() => {
    if (!editingEdge) return null;
    const found = routes.find((r) => r.edge.id === editingEdge);
    if (!found) return null;
    return { edge: found.edge, at: pointAtFraction(found.points, found.edge.labelPos ?? 0.5) };
  }, [editingEdge, routes]);

  return (
    <div
      className={`dgm${action ? ` is-${action.type}` : ''}`}
      ref={hostRef}
      // Focusable but not a tab stop: it has to be able to hold focus so
      // the shortcuts above know which chart they belong to, without
      // adding a stop that a keyboard user cannot do anything with.
      tabIndex={-1}
      role="group"
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      // Take the cursor off other people's charts when it leaves this
      // one, rather than leaving it parked on whatever it was over.
      onPointerLeave={() => onPointerReport?.(null)}
      onContextMenu={(e) => e.preventDefault()}
      onDragOver={(e) => { if (onDropShape) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
      onDrop={(e) => {
        if (!onDropShape) return;
        e.preventDefault();
        const box = hostRef.current.getBoundingClientRect();
        onDropShape(toWorld(viewport, e.clientX - box.left, e.clientY - box.top));
      }}
      onDoubleClick={(e) => {
        if (readOnly) return;
        const world = pointerWorld(e);
        const shape = shapeAt(doc, world, hitShape);
        if (shape) { setSelection([shape.id]); setEditing(shape.id); return; }
        // The same gesture on a line edits what the line says.
        const near = routes.find((r) => distanceToPath(world, r.points) <= EDGE_SLOP / viewport.scale);
        if (near) { setSelection([near.edge.id]); setEditingEdge(near.edge.id); return; }

        // And on bare canvas it starts a note, with the caret already in
        // it. Annotating a chart meant finding the shape library, finding
        // Text in it, dragging one out and then double-clicking that —
        // four steps for a sentence somebody wants to write NOW, which is
        // why charts came back with the queries written down elsewhere.
        //
        // Red and bold because of what these are FOR: everything else on
        // a chain of title is the record, and this is somebody's remark
        // about it. A note that looked like a party would be read as one.
        const out = addShape(doc, {
          shapeType: 'text', text: '',
          x: world.x - NOTE.w / 2, y: world.y - NOTE.h / 2, w: NOTE.w, h: NOTE.h,
          fill: 'transparent', stroke: 'transparent',
          textColor: NOTE.color, bold: true, fontSize: NOTE.fontSize,
        });
        onChange?.(out.doc);
        setSelection([out.shape.id]);
        setEditing(out.shape.id);
        // Not committed to the undo list yet: an empty note is not a
        // change anybody made, and the commit happens below if something
        // is actually typed into it.
        setBlankNote(out.shape.id);
      }}
      data-background={background}
      style={{
        // The grid is painted by CSS and offset by the viewport, so it
        // pans and zooms with the diagram rather than sitting still
        // underneath it. Sized from the same constant a drag snaps to: a
        // grid you can see but cannot land on is worse than no grid.
        backgroundSize: `${GRID * viewport.scale}px ${GRID * viewport.scale}px`,
        backgroundPosition: `${viewport.x}px ${viewport.y}px`,
      }}
    >
      <svg className="dgm-surface" width={size.w} height={size.h}>
        <defs>
          <marker id="dgm-arrow" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
          </marker>
        </defs>
        <g transform={`translate(${viewport.x} ${viewport.y}) scale(${viewport.scale})`}>
          {routes.map(({ edge, points }) => (
            <g key={edge.id} className={`dgm-edge${chosen.has(edge.id) ? ' is-on' : ''}${marked.has(edge.id) ? ' is-flagged' : ''}`}>
              <path
                d={pathFrom(points, edge.lineShape === 'elbow' ? edge.rounding : 0)}
                fill="none"
                stroke={edge.stroke}
                strokeWidth={edge.strokeWidth}
                strokeDasharray={dashOf(edge.strokeStyle, edge.strokeWidth)}
                markerEnd={edge.endCap === 'arrow' ? 'url(#dgm-arrow)' : undefined}
                markerStart={edge.startCap === 'arrow' ? 'url(#dgm-arrow)' : undefined}
              />
            </g>
          ))}

          {/* Under the shapes, so a flagged box is ringed rather than
              covered — and the fill it was given on purpose still
              reads. */}
          {marked.size > 0 && doc.shapes.filter((shape) => marked.has(shape.id)).map((shape) => (
            <rect
              key={`flag-${shape.id}`}
              className="dgm-flag"
              x={shape.x - 6} y={shape.y - 6}
              width={shape.w + 12} height={shape.h + 12}
              rx={6}
              transform={shape.rotation
                ? `rotate(${shape.rotation} ${shape.x + shape.w / 2} ${shape.y + shape.h / 2})`
                : undefined}
              strokeWidth={3 / viewport.scale}
            />
          ))}

          {drawn.map((shape) => (
            <Shape
              key={shape.id}
              shape={shape}
              selected={chosen.has(shape.id)}
              hovered={hover === shape.id}
              hidden={editing === shape.id}
            />
          ))}

          {/* Above the shapes, so a tag on a box that overlaps another
              box is still reachable, and below the labels and ports for
              the same reasons those sit where they do. */}
          {onOpenDocument && doc.shapes
            .filter((shape) => shape.documentId && editing !== shape.id)
            .map((shape) => (
              <DocumentTag
                key={`tag-${shape.id}`}
                shape={shape}
                scale={viewport.scale}
                onOpen={(s) => onOpenDocument(s.documentId, s)}
              />
            ))}

          {action?.type === 'connect' && (
            <ConnectPreview
              action={action}
              shapesById={shapesById}
              scale={viewport.scale}
              landing={landing}
            />
          )}

          {/* Grips to bend a connector by hand, on the selected one
              only — every segment of every line would otherwise be
              covered in handles. */}
          {!readOnly && selection.length === 1 && routes
            .filter((r) => r.edge.id === selection[0])
            .map(({ edge, points }) => (
              <g key={edge.id}>
                <WaypointGrips
                  edge={edge}
                  points={points}
                  scale={viewport.scale}
                  onDown={onWaypointDown}
                  onClear={() => commit(updateEdges(doc, [edge.id], { waypoints: [] }), 'Straighten')}
                />
                {/* After the waypoints, so where they meet at the end of
                    a short leg the end wins the pointer. Moving a line
                    onto the right party matters more than bending it. */}
                <EndGrips
                  points={points}
                  scale={viewport.scale}
                  onDown={(e, which) => onEndDown(e, edge, which, points)}
                />
              </g>
            ))}

          {/* Where the end would land, from the end that is staying put. */}
          {action?.type === 'endpoint' && (
            <path
              className="dgm-connect-preview"
              d={`M ${action.anchor.x} ${action.anchor.y} L ${(landing?.at || action.at).x} ${(landing?.at || action.at).y}`}
              strokeWidth={2 / viewport.scale}
              markerEnd="url(#dgm-arrow)"
            />
          )}

          {frame && !action?.moved && (
            <SelectionFrame
              frame={frame}
              scale={viewport.scale}
              readOnly={readOnly}
              onHandleDown={onHandleDown}
              multiple={selection.length > 1}
            />
          )}

          {/* The four points a connector can leave from, on the shape
              under the pointer.
              AFTER the selection frame and OUTSIDE the shape's edge, both
              deliberately: a port at an edge midpoint sits exactly where
              the n/e/s/w resize handle is, so on a selected shape the
              handle covered it and there was no way to start a line from
              the box you had just clicked. */}
          {!readOnly && hover && !action && shapesById.get(hover) && (
            <ConnectPoints
              shape={shapesById.get(hover)}
              scale={viewport.scale}
              onDown={onConnectDown}
            />
          )}

          {/* And the same four points on the box a line is being dragged
              ONTO, with the one it would land on filled in.
              The box was already highlighted, which said WHETHER it
              would attach but never WHERE — and where is the thing being
              chosen: an arrow into the top of a grantee and one into its
              left side route differently and read differently on a
              printed chain. Showing the port before the drop also means
              the side can be picked by moving the pointer round the box
              rather than discovered afterwards. */}
          {landing && (
            <ConnectPoints
              shape={landing.shape}
              scale={viewport.scale}
              active={landing.side}
              activeAt={landing.at}
            />
          )}

          {guides.lines.map((line, i) => (
            <line
              key={`g${i}`}
              className="dgm-guide"
              x1={line.axis === 'x' ? line.at : line.from}
              x2={line.axis === 'x' ? line.at : line.to}
              y1={line.axis === 'x' ? line.from : line.at}
              y2={line.axis === 'x' ? line.to : line.at}
              strokeWidth={1 / viewport.scale}
            />
          ))}
          {guides.spacing.map((span, i) => (
            <line
              key={`s${i}`}
              className="dgm-spacing"
              x1={span.axis === 'x' ? span.from : span.at}
              x2={span.axis === 'x' ? span.to : span.at}
              y1={span.axis === 'x' ? span.at : span.from}
              y2={span.axis === 'x' ? span.at : span.to}
              strokeWidth={1 / viewport.scale}
            />
          ))}
          {/* A matched size is not an alignment, so it is not drawn as a
              guide line — nothing is in line. It is two measure bars, one
              under the box being sized and one under the box it now
              matches, which says the thing that is actually true: these
              two are the same width. */}
          {guides.sizes.map((match, i) => match.bars.map((bar, j) => (
            <line
              key={`z${i}-${j}`}
              className="dgm-size-match"
              x1={match.axis === 'x' ? bar.from : bar.at}
              x2={match.axis === 'x' ? bar.to : bar.at}
              y1={match.axis === 'x' ? bar.at : bar.from}
              y2={match.axis === 'x' ? bar.at : bar.to}
              strokeWidth={2 / viewport.scale}
            />
          )))}

          {/* The instruments, TOPMOST.
              Above the shapes, because a label near either end of a line
              otherwise falls under the party box there. And above the
              connection ports, because a label parked near a shape lands
              exactly on that shape's port — and if the port wins the
              pointer, the label can never be dragged out of the way,
              which is the one move that resolves the collision. A port
              covered by a label is reachable from the other three sides;
              a label under a port is stuck for good. */}
          {routes.map(({ edge, points }) => (
            edge.label && editingEdge !== edge.id ? (
              <EdgeLabel
                key={`l${edge.id}`}
                edge={edge}
                at={pointAtFraction(points, edge.labelPos ?? 0.5)}
                scale={viewport.scale}
                readOnly={readOnly}
                onDown={(e) => onLabelDown(e, edge, points)}
              />
            ) : null
          ))}

          {/* ABOVE EVERYTHING, and never in the way. Somebody else's
              pointer is information, not a control: it must be readable
              over a box, and it must not be clickable — a cursor that
              swallowed a click would make the chart feel broken in a way
              nobody would connect to the person who caused it. */}
          {peers?.length > 0 && (
            <g className="dgm-peers">
              {peers.map((peer) => (
                <PeerCursor key={peer.id} peer={peer} scale={viewport.scale} />
              ))}
            </g>
          )}

          {action?.type === 'marquee' && (() => {
            // Spread out by hand, because the rect this returns calls
            // them w and h and SVG calls them width and height. Spreading
            // it put w="260" h="400" on the element, which are not
            // attributes of anything: the band selected correctly and was
            // drawn 0 by 0, so rubber-banding has never shown a rectangle.
            const band = rectFromPoints(action.start, action.at);
            return (
              <rect
                className="dgm-marquee"
                x={band.x} y={band.y} width={band.w} height={band.h}
                strokeWidth={1 / viewport.scale}
              />
            );
          })()}
        </g>
      </svg>

      {editEdge && (
        <EdgeLabelEditor
          edge={editEdge.edge}
          at={screen(editEdge.at.x, editEdge.at.y)}
          scale={viewport.scale}
          onDone={(label) => {
            setEditingEdge(null);
            if (label !== editEdge.edge.label) {
              commit(updateEdges(doc, [editEdge.edge.id], { label }), 'Instrument');
            }
          }}
        />
      )}

      {editShape && (
        <TextEditor
          shape={editShape}
          at={screen(editShape.x, editShape.y)}
          scale={viewport.scale}
          onDone={(text) => {
            setEditing(null);
            const wasBlank = blankNote === editShape.id;
            setBlankNote(null);
            // A note double-clicked into and then left empty is removed
            // rather than kept: an invisible transparent box with nothing
            // in it is impossible to find again and impossible to click
            // past, and double-clicking around a chart would leave a
            // trail of them.
            if (wasBlank && !text.trim()) { onChange?.(removeItems(doc, [editShape.id])); return; }
            if (wasBlank) { commit(updateShapes(doc, [editShape.id], { text }), 'Note'); return; }
            if (text !== editShape.text) commit(updateShapes(doc, [editShape.id], { text }), 'Text');
          }}
        />
      )}

      <div className="dgm-zoom dgm-ui">
        <button type="button" onClick={() => setViewport((v) => zoomAbout(v, size.w / 2, size.h / 2, v.scale * 1.25))} aria-label="Zoom in">+</button>
        <button type="button" onClick={() => setViewport((v) => zoomAbout(v, size.w / 2, size.h / 2, v.scale / 1.25))} aria-label="Zoom out">−</button>
        <button type="button" onClick={fit} aria-label="Fit to screen">Fit</button>
        <span>{Math.round(viewport.scale * 100)}%</span>
      </div>
    </div>
  );
}

/** One shape: its outline, then its text wrapped to fit inside it. */
function ShapeBody({ shape, selected, hovered, hidden }) {
  const path = shapePath(shape.shapeType, shape.w, shape.h);
  // Wrapped rather than laid out as written: "Marion Emma Cornell, as
  // Life Estate" in a 160-wide box runs out of both sides and over the
  // party next to it, which on a dense chain is where it hurts most.
  const lines = layoutText(shape, measure);
  const lineHeight = shape.fontSize * 1.25;
  const startY = firstBaseline(shape, lines.length);
  const textX = textAnchorX(shape);
  const anchor = shape.align === 'left' ? 'start' : shape.align === 'right' ? 'end' : 'middle';

  return (
    <g
      className={`dgm-shape${selected ? ' is-on' : ''}${hovered ? ' is-hover' : ''}`}
      transform={`translate(${shape.x} ${shape.y})${shape.rotation ? ` rotate(${shape.rotation} ${shape.w / 2} ${shape.h / 2})` : ''}`}
      opacity={hidden ? 0 : 1}
    >
      {path && (
        <path
          d={path}
          fill={shape.fill === 'transparent' ? 'none' : shape.fill}
          stroke={shape.stroke === 'transparent' ? 'none' : shape.stroke}
          strokeWidth={shape.strokeWidth}
          strokeDasharray={dashOf(shape.strokeStyle, shape.strokeWidth)}
        />
      )}
      {/* A shape with no fill still has to be clickable across its face,
          or a text annotation can only be grabbed by its letters. */}
      {shape.fill === 'transparent' && (
        <rect width={shape.w} height={shape.h} fill="transparent" />
      )}
      <text
        fontSize={shape.fontSize}
        fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
        fill={shape.textColor}
        fontWeight={shape.bold ? 700 : 400}
        fontStyle={shape.italic ? 'italic' : undefined}
        textDecoration={shape.underline ? 'underline' : undefined}
        textAnchor={anchor}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={textX} y={startY + i * lineHeight}>{line || ' '}</tspan>
        ))}
      </text>
    </g>
  );
}

/**
 * Redrawn only when it has actually changed.
 *
 * Every pointer move of a drag renders the canvas, and without this that
 * is three hundred boxes laid out and wrapped again to move one. The
 * document helpers keep the identity of anything they did not touch, so
 * the comparison React does here is a reference check that almost always
 * says no — and on a section chain it is the difference between a drag
 * that follows the pointer and one that trails behind it.
 */
const Shape = memo(ShapeBody);

/**
 * Somebody else's pointer, with their name on it.
 *
 * A CONSTANT SIZE ON SCREEN, like the ports and the document tags. A
 * cursor that scaled with the diagram would be a speck at the zoom
 * somebody uses to see a whole section and the size of a party box at
 * the zoom they use to read one deed.
 *
 * The colour comes from who they are rather than from the order they
 * arrived, so somebody is the same colour for everybody at once and
 * stays that colour when they reload — see presence.js. The name sits
 * below and to the right of the point, where the arrow itself is not,
 * so it never covers the thing being pointed at.
 */
function PeerCursor({ peer, scale }) {
  const s = 1 / scale;
  const colour = cursorColour(peer.id);
  // The arrow, drawn from its point rather than its corner: the point is
  // what means something.
  const arrow = 'M 0 0 L 0 15 L 4 11.5 L 6.6 17 L 9.4 15.7 L 6.7 10.3 L 12 10 z';
  return (
    <g className="dgm-peer" transform={`translate(${peer.x} ${peer.y}) scale(${s})`}>
      <path d={arrow} fill={colour} stroke="#fff" strokeWidth={1.2} strokeLinejoin="round" />
      <g transform="translate(13 17)">
        {/* MEASURED, not counted. Six and a bit pixels a character is
            right for "Ada" and wrong for "Mae Handley", where the name
            hangs out of the end of its own tab — and every name on a
            roster of agents is a different width. */}
        <rect
          className="dgm-peer-tab"
          x={0} y={0} rx={3}
          width={measure(peer.name, PEER_FONT) + 12} height={17}
          fill={colour}
        />
        <text className="dgm-peer-name" x={6} y={12} fontSize={11} fill="#fff">
          {peer.name}
        </text>
      </g>
    </g>
  );
}

/**
 * The instrument a box was drawn from, one click away.
 *
 * Every deed block built from a runsheet already knows which recorded
 * document it came from — the id has been carried on the shape since the
 * importer was written — and until now nothing read it. An examiner
 * reading a chain asks "show me that deed" about every second box, and
 * the answer was to leave the chart, find the document list, and search
 * for a volume and page that is written on the box they were looking at.
 *
 * OUTSIDE the top-right corner, on the diagonal. The corner itself is a
 * resize handle and the edge midpoints are connection ports, so the
 * diagonal is the one direction around a shape where nothing else lives.
 * It follows the rotation, because a tag that stays upright while its
 * box turns ends up sitting over the middle of a rotated shape.
 *
 * Shown whether or not the chart can be edited: a client looking at a
 * read-only chain has more reason to open the deeds, not less.
 */
function DocumentTag({ shape, scale, onOpen }) {
  const corner = cornersOf(shape)[1];
  const cx = centerX(shape);
  const cy = centerY(shape);
  const dx = corner.x - cx;
  const dy = corner.y - cy;
  const len = Math.hypot(dx, dy) || 1;
  const out = TAG_OUT / scale;
  const at = { x: corner.x + (dx / len) * out, y: corner.y + (dy / len) * out };

  // A constant size on screen. A tag that scales with the diagram is
  // unreadable at the zoom somebody uses to see a whole section and
  // absurd at the zoom they use to read one deed.
  const r = 7 / scale;
  const w = 5 / scale;
  const h = 6.4 / scale;
  const fold = 1.8 / scale;
  const page = `M ${at.x - w / 2} ${at.y - h / 2}`
    + ` h ${w - fold} l ${fold} ${fold} v ${h - fold} h ${-w} z`;

  const open = (event) => { event.stopPropagation(); onOpen(shape); };

  return (
    <g
      className="dgm-doc-tag"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={open}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <title>Open the recorded document</title>
      {/* The same trick as the ports: an invisible disc wider than the
          mark, because a 14px target is 7px of screen at half zoom. */}
      <circle className="dgm-doc-tag-hit" cx={at.x} cy={at.y} r={r * 1.7} />
      <circle
        className="dgm-doc-tag-disc" cx={at.x} cy={at.y} r={r}
        strokeWidth={1.2 / scale}
      />
      <path className="dgm-doc-tag-page" d={page} strokeWidth={1 / scale} />
    </g>
  );
}

/**
 * Four dots just outside a shape's edges, each the start of a connector.
 *
 * OUTSIDE by a fixed number of screen pixels rather than on the edge
 * itself. On the edge they coincide exactly with the n/e/s/w resize
 * handles of a selected shape, and the handle wins the pointer — so the
 * one box somebody has just clicked is the one box they cannot draw a
 * line from, which is the wrong way round.
 *
 * Each dot carries an invisible disc twice its size. A 4px target at 60%
 * zoom is two pixels of screen, and a connector nobody can grab is a
 * connector that does not exist.
 */
function ConnectPoints({ shape, scale, onDown, active = null, activeAt = null }) {
  const rect = { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
  const r = 4.5 / scale;
  const out = PORT_OUT / scale;
  const shift = {
    top: { x: 0, y: -out }, bottom: { x: 0, y: out },
    left: { x: -out, y: 0 }, right: { x: out, y: 0 },
  };
  // Shown mid-drag as well as on hover, and mid-drag it must not take
  // the pointer: the pointer belongs to the line being dragged, and a
  // port that stole a move would drop the line where it stood.
  const live = Boolean(onDown);
  return (
    <g className={live ? 'dgm-ports' : 'dgm-ports is-landing'}>
      {SIDES.map((side) => {
        const on = side === active;
        // The lit one is drawn WHERE THE LINE WILL LAND, which is not
        // the middle of the side any more: an endpoint slides along it.
        // The other three stay at their midpoints, where they are only
        // saying "this side is available".
        const p = on && activeAt ? activeAt : sidePoint(rect, side);
        const at = { x: p.x + shift[side].x, y: p.y + shift[side].y };
        return (
          <g key={side} onPointerDown={live ? (e) => onDown(e, shape, side) : undefined}>
            {live && <circle className="dgm-port-hit" cx={at.x} cy={at.y} r={r * 2.4} />}
            <circle
              className={on ? 'dgm-port is-live' : 'dgm-port'}
              cx={at.x}
              cy={at.y}
              // The one it would land on is grown, for the same reason
              // the hovered one is: the dot is deliberately small, and
              // at 40% zoom the difference between filled and hollow is
              // not a difference anybody can see.
              r={on ? r * 1.7 : r}
              strokeWidth={1.5 / scale}
            />
          </g>
        );
      })}
    </g>
  );
}

/**
 * The line being dragged.
 *
 * It follows the pointer over empty canvas and SNAPS to the port it
 * would attach to once it is over a box — the same snap the drop
 * performs, half a second earlier, where it can still be reconsidered.
 */
function ConnectPreview({ action, shapesById, scale, landing }) {
  const shape = shapesById.get(action.from.shapeId);
  if (!shape) return null;
  const from = sidePoint({ x: shape.x, y: shape.y, w: shape.w, h: shape.h }, action.from.side);
  const to = landing?.at || action.at;
  return (
    <path
      className="dgm-connect-preview"
      d={`M ${from.x} ${from.y} L ${to.x} ${to.y}`}
      strokeWidth={2 / scale}
      markerEnd="url(#dgm-arrow)"
    />
  );
}

/**
 * The two ends of a selected connector, draggable onto somewhere else.
 *
 * A conveyance drawn to the wrong party is the commonest mistake on a
 * chain — grantor and grantee are two boxes apart and the arrow goes to
 * the neighbour — and until now the only fix was to delete the line and
 * draw it again, which also loses the instrument typed on it. Now the
 * end moves and the deed stays.
 *
 * BIGGER THAN A WAYPOINT, and a ring rather than a dot. The two sit on
 * the same line a few pixels apart and do entirely different things: one
 * bends the route, the other changes what the line MEANS. They must not
 * be mistaken for each other.
 */
function EndGrips({ points, scale, onDown }) {
  const r = 5.5 / scale;
  const ends = [
    { which: 'from', at: points[0] },
    { which: 'to', at: points[points.length - 1] },
  ];
  return (
    <g className="dgm-ends">
      {ends.map(({ which, at }) => (
        <g key={which} onPointerDown={(e) => onDown(e, which)}>
          {/* An invisible disc twice the size: a 5px ring at 60% zoom is
              three pixels of screen, and an end nobody can grab is an end
              that cannot be moved. */}
          <circle className="dgm-end-hit" cx={at.x} cy={at.y} r={r * 2.2} />
          <circle
            className="dgm-end" cx={at.x} cy={at.y} r={r}
            strokeWidth={2 / scale}
          />
        </g>
      ))}
    </g>
  );
}

/**
 * Grips along a selected connector.
 *
 * A HOLLOW grip at each segment's midpoint adds a bend; a SOLID one is a
 * bend somebody already made, and double-clicking it takes it out again.
 * The distinction matters because a line that can only gain waypoints is
 * a line that slowly becomes unroutable.
 */
function WaypointGrips({ edge, points, scale, onDown, onClear }) {
  const r = 4 / scale;
  const placed = edge.waypoints || [];
  const mids = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    if (Math.hypot(b.x - a.x, b.y - a.y) * scale < 24) continue;
    mids.push({ at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, index: i });
  }
  return (
    <g className="dgm-waypoints">
      {placed.length === 0 && mids.map((m) => (
        <circle
          key={`m${m.index}`}
          className="dgm-waypoint is-new"
          cx={m.at.x}
          cy={m.at.y}
          r={r}
          strokeWidth={1.5 / scale}
          onPointerDown={(e) => onDown(e, edge, 0, m.at)}
        />
      ))}
      {placed.map((point, i) => (
        <circle
          key={`w${i}`}
          className="dgm-waypoint"
          cx={point.x}
          cy={point.y}
          r={r}
          strokeWidth={1.5 / scale}
          onPointerDown={(e) => onDown(e, edge, i, null)}
          onDoubleClick={(e) => { e.stopPropagation(); onClear(); }}
        />
      ))}
    </g>
  );
}

/**
 * What the conveyance says, typed on the line.
 *
 * Centred on the midpoint rather than anchored to a corner, because that
 * is where the label will be drawn — an editor that opens somewhere else
 * moves the text as you finish typing it.
 *
 * Sized in SCREEN pixels with a floor: at 16% zoom, which is what a
 * whole section chain is read at, a box scaled with the diagram would be
 * four pixels tall and unusable — and the whole point is to type into it
 * the moment the line is drawn.
 */
function EdgeLabelEditor({ edge, at, scale, onDone }) {
  const ref = useRef(null);
  const [text, setText] = useState(edge.label || '');

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const width = Math.max(150, 170 * scale);
  const height = Math.max(64, 74 * scale);
  return (
    <textarea
      ref={ref}
      className="dgm-edge-edit dgm-ui"
      value={text}
      placeholder={'WD 812/44\n12/16/1977\n1/2 mineral interest'}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onDone(text)}
      onKeyDown={(e) => {
        // Escape abandons, ⌘⏎ commits, plain ⏎ is a new line — a deed is
        // four short lines, so a new line is the common case.
        if (e.key === 'Escape') { e.preventDefault(); onDone(edge.label || ''); }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onDone(text); }
        e.stopPropagation();
      }}
      style={{
        left: at.x - width / 2,
        top: at.y - height / 2,
        width,
        height,
        fontSize: Math.max(9, (edge.fontSize || 8) * scale),
      }}
    />
  );
}

/**
 * The instrument, drawn on its line.
 *
 * PAINTED ON AN OPAQUE CARD, so the connector genuinely disappears
 * behind it. A halo round the letters — which is what this used to do —
 * leaves the line running between the words, and a deed reading
 * "WD 812/44" with a rule through it is harder to read than one with a
 * clean gap behind it. The card is measured from the real text rather
 * than guessed at, because a fixed width would either clip a long land
 * description or punch a hole in the line for a label saying "1/2".
 *
 * The whole thing is draggable: on a dense chart the middle of one line
 * lands on top of the next one along, and moving the label is the only
 * way out.
 */
function EdgeLabel({ edge, at, scale, readOnly, onDown }) {
  const lines = String(edge.label).split('\n');
  const size = edge.fontSize || 8;
  const lineHeight = size * 1.25;
  const top = at.y - (lineHeight * (lines.length - 1)) / 2;

  const font = `${edge.bold ? '700' : '400'} ${size}px Inter, ui-sans-serif, system-ui, sans-serif`;
  const widest = Math.max(...lines.map((line) => measure(line, font)), 0);
  const padX = 4;
  const padY = 3;
  const boxW = widest + padX * 2;
  const boxH = lineHeight * lines.length + padY * 2 - (lineHeight - size) / 2;

  return (
    <g
      className={`dgm-edge-label-group${readOnly ? '' : ' is-draggable'}`}
      onPointerDown={readOnly ? undefined : onDown}
    >
      <rect
        className="dgm-edge-label-card"
        x={at.x - boxW / 2}
        y={top - size - padY + (lineHeight - size) / 2}
        width={boxW}
        height={boxH}
        rx={2 / (scale || 1)}
      />
      <text
        className="dgm-edge-label"
        fontSize={size}
        fontWeight={edge.bold ? 700 : 400}
        fontStyle={edge.italic ? 'italic' : undefined}
        textDecoration={edge.underline ? 'underline' : undefined}
        fill={edge.labelColor}
        textAnchor="middle"
      >
        {lines.map((line, i) => (
          <tspan key={i} x={at.x} y={top + i * lineHeight}>{line}</tspan>
        ))}
      </text>
    </g>
  );
}

/**
 * The eight handles and the rotation arm.
 *
 * Sized in inverse scale so a handle is the same number of SCREEN pixels
 * at every zoom — otherwise they are unusable specks at 16% and cover
 * the shape at 400%.
 */
function SelectionFrame({ frame, scale, readOnly, onHandleDown, multiple }) {
  const s = 1 / scale;
  const size = 8 * s;
  const rotationOf = frame.rotation
    ? `rotate(${frame.rotation} ${frame.w / 2} ${frame.h / 2})`
    : '';
  return (
    <g className="dgm-frame" transform={`translate(${frame.x} ${frame.y})${rotationOf}`}>
      <rect
        width={frame.w}
        height={frame.h}
        fill="none"
        strokeWidth={s}
        className={multiple ? 'dgm-frame-box is-multi' : 'dgm-frame-box'}
      />
      {!readOnly && (
        <>
          <line x1={frame.w / 2} y1={0} x2={frame.w / 2} y2={-ROTATE_ARM * s} strokeWidth={s} className="dgm-rot-arm" />
          <circle
            cx={frame.w / 2} cy={-ROTATE_ARM * s} r={size * 0.6}
            className="dgm-handle dgm-rotate"
            onPointerDown={(e) => onHandleDown(e, 'rotate')}
          />
          {HANDLES.map((handle) => {
            const p = handlePoint({ x: 0, y: 0, w: frame.w, h: frame.h }, handle);
            return (
              <rect
                key={handle}
                className={`dgm-handle dgm-h-${handle}`}
                x={p.x - size / 2}
                y={p.y - size / 2}
                width={size}
                height={size}
                strokeWidth={s}
                onPointerDown={(e) => onHandleDown(e, handle)}
              />
            );
          })}
        </>
      )}
    </g>
  );
}

/**
 * Editing a shape's text in place.
 *
 * A real textarea positioned over the shape rather than a
 * contentEditable SVG: newlines matter here — every party box is a name
 * over a date over a recording reference — and a textarea is the one
 * control that handles them without any of its own machinery.
 */
function TextEditor({ shape, at, scale, onDone }) {
  const ref = useRef(null);
  const [text, setText] = useState(shape.text);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <textarea
      ref={ref}
      className="dgm-text-edit dgm-ui"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onDone(text)}
      onKeyDown={(e) => {
        // Escape abandons, ⌘⏎ commits, plain ⏎ is a new line — because
        // in these boxes a new line is the common case.
        if (e.key === 'Escape') { e.preventDefault(); onDone(shape.text); }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onDone(text); }
        e.stopPropagation();
      }}
      style={{
        left: at.x,
        top: at.y,
        width: shape.w * scale,
        height: shape.h * scale,
        fontSize: shape.fontSize * scale,
        lineHeight: 1.25,
        fontWeight: shape.bold ? 700 : 400,
        color: shape.textColor,
        textAlign: shape.align,
      }}
    />
  );
}
